// src/pages/cocos/CocoDashboard.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  writeBatch, query, orderBy, where, deleteDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

// ─── Survey question IDs (hardcoded from the coco survey) ──────────────────
const Q_GRADE    = '64bbd7b7-40fd-4e71-8ea1-652b585cf9d0';
const Q_BBALL    = 'cd4d0a7e-2f7b-4c35-bb97-f3c08b070763';
const Q_TEAM     = '230adb8e-393c-4462-bca3-221346fcfeac';
const Q_CHOICE1  = '76874998-90a7-45fe-a383-62bd147a7b78';
const Q_CHOICE2  = '8a45c347-baff-452b-bba7-b99a24268725';
const Q_CHOICE3  = 'e89620ad-1f53-4557-96dd-3ad3aa60c67b';

const ACTIVITIES = [
  'Gym/fitness', 'Fit Walking', 'Golf', 'Squash', 'Padel', 'Tennis',
  'Swimming', 'Boxing', 'Ultimate Frisbee', 'Horseback Riding',
  'Fencing', 'Dabke & Contemporary Dance', 'Yoga',
];

const TEAM_SPORT_VALUES = ['Volleyball', 'Football', 'Both Volleyball and Football'];

const STATUS_LABELS = {
  active:           { label: 'In Pool',         color: '#2563eb' },
  basketball:       { label: 'Basketball Tryout', color: '#d97706' },
  exempt:           { label: 'Exempt',           color: '#6b7280' },
  offseason:        { label: 'Offseason Training', color: '#7c3aed' },
  'offseason-opted-in': { label: 'Offseason → Cocos', color: '#059669' },
  unplaced:         { label: 'Unplaced',         color: '#dc2626' },
};

const SEASON_ID = 'semester2-2025-2026';

// ─── Styles ────────────────────────────────────────────────────────────────
const s = {
  page: { maxWidth: 1200, margin: '0 auto', padding: '28px 24px', fontFamily: 'var(--font-body)' },
  h1:   { fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 4 },
  sub:  { fontSize: 13, color: 'var(--ka-gray-500)', marginBottom: 28 },
  tabs: { display: 'flex', gap: 4, borderBottom: '2px solid var(--ka-gray-200)', marginBottom: 28 },
  tab:  (active) => ({
    padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: 'none', borderBottom: active ? '2px solid var(--ka-navy)' : '2px solid transparent',
    color: active ? 'var(--ka-navy)' : 'var(--ka-gray-500)', marginBottom: -2,
  }),
  card:   { background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)', borderRadius: 10, padding: 20, marginBottom: 16 },
  row:    { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  label:  { fontSize: 12, fontWeight: 600, color: 'var(--ka-gray-500)', marginBottom: 4, display: 'block' },
  input:  { padding: '7px 10px', border: '1px solid var(--ka-gray-300)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--font-body)' },
  select: { padding: '7px 10px', border: '1px solid var(--ka-gray-300)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--font-body)', background: 'var(--ka-white)' },
  btn:    (variant = 'primary') => ({
    padding: '8px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: variant === 'primary' ? 'var(--ka-navy)' : variant === 'danger' ? '#dc2626' : variant === 'success' ? '#059669' : 'var(--ka-gray-100)',
    color: variant === 'ghost' ? 'var(--ka-gray-700)' : '#fff',
  }),
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: color + '20', color: color,
  }),
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:    { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--ka-gray-200)', fontWeight: 600, color: 'var(--ka-gray-600)', fontSize: 12 },
  td:    { padding: '8px 10px', borderBottom: '1px solid var(--ka-gray-100)', verticalAlign: 'middle' },
  stat:  { textAlign: 'center', padding: '12px 20px', background: 'var(--ka-gray-50)', borderRadius: 8 },
  statN: { fontSize: 28, fontWeight: 700, color: 'var(--ka-navy)' },
  statL: { fontSize: 11, color: 'var(--ka-gray-500)', marginTop: 2 },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 12 },
  warn: { background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#92400e', marginBottom: 12 },
  success: { background: '#d1fae5', border: '1px solid #059669', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#065f46', marginBottom: 12 },
};

// ─── Helpers ───────────────────────────────────────────────────────────────
function parseCSVToEmails(text) {
  return text.split(/\r?\n/).flatMap(line =>
    line.split(',').map(v => v.trim().toLowerCase()).filter(v => v.includes('@'))
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function CocoDashboard() {
  const { isSuperAdmin, profile } = useAuth();
  const canManage = isSuperAdmin || profile?.role === 'admin';

  const [tab, setTab] = useState('setup');
  const [config, setConfig] = useState(null);
  const [roster, setRoster] = useState({});       // { sisId: rosterDoc }
  const [activities, setActivities] = useState({}); // { name: { capacity, staff[] } }
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);           // { type: 'success'|'warn', text }
  const [saving, setSaving] = useState(false);

  // ── Load from Firestore ─────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    const [cfgSnap, rosterSnap, actSnap] = await Promise.all([
      getDoc(doc(db, 'cocoSeasons', SEASON_ID)),
      getDocs(collection(db, 'cocoSeasons', SEASON_ID, 'roster')),
      getDocs(collection(db, 'cocoSeasons', SEASON_ID, 'activities')),
    ]);
    setConfig(cfgSnap.exists() ? cfgSnap.data() : { status: 'draft', label: 'Semester 2 2025–2026' });
    const r = {};
    rosterSnap.docs.forEach(d => { r[d.id] = d.data(); });
    setRoster(r);
    const a = {};
    actSnap.docs.forEach(d => { a[d.id] = d.data(); });
    // Fill in any missing activities with defaults
    ACTIVITIES.forEach(name => { if (!a[name]) a[name] = { capacity: 20, staff: [] }; });
    setActivities(a);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  if (loading) return <div style={s.page}><p style={{ color: 'var(--ka-gray-500)' }}>Loading…</p></div>;

  const rosterList = Object.entries(roster).map(([id, d]) => ({ id, ...d }));
  const committed = config?.status === 'committed';

  return (
    <div style={s.page}>
      <div style={s.row}>
        <div>
          <h1 style={s.h1}>Co-Curricular Scheduler</h1>
          <p style={s.sub}>{config?.label} · {rosterList.length} students loaded</p>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          {committed
            ? <span style={s.badge('#059669')}>✓ Committed</span>
            : <span style={s.badge('#d97706')}>Draft</span>}
        </div>
      </div>

      {msg && <div style={msg.type === 'success' ? s.success : s.warn}>{msg.text}</div>}

      <div style={s.tabs}>
        {[['setup', 'Setup & Roster'], ['activities', 'Activities & Staff'], ['scheduler', 'Scheduler'], ['results', 'Results']].map(([k, l]) => (
          <button key={k} style={s.tab(tab === k)} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'setup' && (
        <SetupTab
          config={config} roster={roster} rosterList={rosterList}
          setRoster={setRoster} setConfig={setConfig}
          canManage={canManage} flash={flash} saving={saving} setSaving={setSaving}
        />
      )}
      {tab === 'activities' && (
        <ActivitiesTab
          activities={activities} setActivities={setActivities}
          canManage={canManage} flash={flash} saving={saving} setSaving={setSaving}
        />
      )}
      {tab === 'scheduler' && (
        <SchedulerTab
          roster={roster} setRoster={setRoster} activities={activities}
          config={config} setConfig={setConfig}
          canManage={canManage} flash={flash} committed={committed}
          saving={saving} setSaving={setSaving}
        />
      )}
      {tab === 'results' && (
        <ResultsTab roster={rosterList} activities={activities} config={config} flash={flash} saving={saving} setSaving={setSaving} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 1 — Setup & Roster
// ═══════════════════════════════════════════════════════════════════════════
function SetupTab({ config, roster, rosterList, setRoster, setConfig, canManage, flash, saving, setSaving }) {
  const [surveyId, setSurveyId] = useState(config?.surveyId || '');
  const [exemptSurveyId, setExemptSurveyId] = useState(config?.exemptionsSurveyId || '');
  const [ingestingMain, setIngestingMain] = useState(false);
  const [ingestingExempt, setIngestingExempt] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [teamSport, setTeamSport] = useState('Volleyball');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGrade, setFilterGrade] = useState('all');
  const [search, setSearch] = useState('');
  const fileRef = useRef();

  // ── Ingest main survey ──────────────────────────────────────────────────
  const ingestMainSurvey = async () => {
    if (!surveyId.trim()) return flash('warn', 'Enter the main survey ID first.');
    setIngestingMain(true);
    try {
      // Save survey ID to config
      await setDoc(doc(db, 'cocoSeasons', SEASON_ID), {
        surveyId: surveyId.trim(),
        label: 'Semester 2 2025–2026',
        status: config?.status || 'draft',
      }, { merge: true });
      setConfig(c => ({ ...c, surveyId: surveyId.trim() }));

      // Fetch survey responses
      const responsesSnap = await getDocs(
        query(collection(db, 'surveys', surveyId.trim(), 'responses'), orderBy('submittedAt', 'desc'))
      );

      if (responsesSnap.empty) { flash('warn', 'No responses found for that survey ID.'); setIngestingMain(false); return; }

      // Build uid→sisId map via /students email lookup
      const responses = responsesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const emails = [...new Set(responses.map(r => r.respondentEmail?.toLowerCase()).filter(Boolean))];
      const emailToSisId = {};
      for (let i = 0; i < emails.length; i += 10) {
        const chunk = emails.slice(i, i + 10);
        const snap = await getDocs(query(collection(db, 'students'), where('emailLower', 'in', chunk)));
        // Try emailLower first; fall back to email field (case-insensitive scan)
        snap.docs.forEach(d => {
          const data = d.data();
          const key = (data.emailLower || data.email || '').toLowerCase();
          if (key) emailToSisId[key] = data.studentSisId || d.id;
        });
      }
      // Fallback: query by email field (mixed case) for misses
      const misses = emails.filter(e => !emailToSisId[e]);
      if (misses.length > 0) {
        for (let i = 0; i < misses.length; i += 10) {
          const chunk = misses.slice(i, i + 10);
          const snap = await getDocs(query(collection(db, 'students'), where('email', 'in', chunk)));
          snap.docs.forEach(d => {
            const data = d.data();
            const key = (data.email || '').toLowerCase();
            if (key) emailToSisId[key] = data.studentSisId || d.id;
          });
        }
        // Last resort: mixed-case originals
        const stillMissing = emails.filter(e => !emailToSisId[e]);
        if (stillMissing.length > 0) {
          const originalEmails = [...new Set(responses.map(r => r.respondentEmail).filter(Boolean))];
          for (let i = 0; i < originalEmails.length; i += 10) {
            const chunk = originalEmails.slice(i, i + 10);
            const snap = await getDocs(query(collection(db, 'students'), where('email', 'in', chunk)));
            snap.docs.forEach(d => {
              const data = d.data();
              const key = (data.email || '').toLowerCase();
              if (key && !emailToSisId[key]) emailToSisId[key] = data.studentSisId || d.id;
            });
          }
        }
      }

      // Write roster docs
      const batch = writeBatch(db);
      const newRoster = { ...roster };
      let written = 0;

      for (const r of responses) {
        const emailKey = r.respondentEmail?.toLowerCase() || '';
        const sisId = emailToSisId[emailKey] || null;
        const docId = sisId || r.id; // fall back to response ID if no sisId found

        const basketball = r.answers?.[Q_BBALL] === 'Yes';
        const teamAnswer = r.answers?.[Q_TEAM] || '';
        const isTeamSport = TEAM_SPORT_VALUES.some(v => teamAnswer.includes(v.split(' ')[0]));

        let status = 'active';
        if (basketball) status = 'basketball';
        else if (isTeamSport) status = 'offseason';

        const rosterDoc = {
          name: r.respondentName || '',
          email: r.respondentEmail || '',
          emailLower: emailKey,
          grade: r.answers?.[Q_GRADE] || '',
          sisId: sisId || '',
          choice1: r.answers?.[Q_CHOICE1] || '',
          choice2: r.answers?.[Q_CHOICE2] || '',
          choice3: r.answers?.[Q_CHOICE3] || '',
          basketball,
          teamSport: isTeamSport ? teamAnswer : null,
          status,
          placed: null,
          placedRound: null,
          manualOverride: null,
          responseId: r.id,
          ingestedAt: new Date().toISOString(),
        };

        batch.set(doc(db, 'cocoSeasons', SEASON_ID, 'roster', docId), rosterDoc, { merge: false });
        newRoster[docId] = rosterDoc;
        written++;
      }

      await batch.commit();
      setRoster(newRoster);
      flash('success', `Ingested ${written} responses. ${Object.values(newRoster).filter(r => !r.sisId).length} students missing SIS ID.`);
    } catch (err) {
      console.error(err);
      flash('warn', 'Ingest failed: ' + err.message);
    }
    setIngestingMain(false);
  };

  // ── Ingest exemptions survey ────────────────────────────────────────────
  const ingestExemptions = async () => {
    if (!exemptSurveyId.trim()) return flash('warn', 'Enter the exemptions survey ID first.');
    setIngestingExempt(true);
    try {
      await setDoc(doc(db, 'cocoSeasons', SEASON_ID), { exemptionsSurveyId: exemptSurveyId.trim() }, { merge: true });
      setConfig(c => ({ ...c, exemptionsSurveyId: exemptSurveyId.trim() }));

      const snap = await getDocs(collection(db, 'surveys', exemptSurveyId.trim(), 'responses'));
      const exemptEmails = new Set(snap.docs.map(d => (d.data().respondentEmail || '').toLowerCase()));

      const batch = writeBatch(db);
      const updated = { ...roster };
      let count = 0;
      for (const [id, student] of Object.entries(updated)) {
        if (exemptEmails.has((student.email || '').toLowerCase())) {
          batch.update(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), { status: 'exempt' });
          updated[id] = { ...student, status: 'exempt' };
          count++;
        }
      }
      await batch.commit();
      setRoster(updated);
      flash('success', `Marked ${count} students as exempt.`);
    } catch (err) {
      flash('warn', 'Exemption ingest failed: ' + err.message);
    }
    setIngestingExempt(false);
  };

  // ── Add team sport players from CSV or dropdown ─────────────────────────
  const addTeamSportPlayers = async () => {
    const emails = parseCSVToEmails(csvText);
    if (!emails.length) return flash('warn', 'No emails found in the CSV text.');
    const batch = writeBatch(db);
    const updated = { ...roster };
    let count = 0;
    for (const [id, student] of Object.entries(updated)) {
      if (emails.includes((student.email || '').toLowerCase())) {
        const newStatus = student.status === 'active' ? 'offseason' : student.status;
        batch.update(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), {
          status: newStatus, teamSport: teamSport,
        });
        updated[id] = { ...student, status: newStatus, teamSport };
        count++;
      }
    }
    await batch.commit();
    setRoster(updated);
    setCsvText('');
    flash('success', `Marked ${count} students as ${teamSport} (offseason).`);
  };

  // ── Manual status toggle ────────────────────────────────────────────────
  const toggleStatus = async (id, newStatus) => {
    await updateDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), { status: newStatus });
    setRoster(r => ({ ...r, [id]: { ...r[id], status: newStatus } }));
  };

  // ── Filtered roster ─────────────────────────────────────────────────────
  const filtered = rosterList.filter(s => {
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    if (filterGrade !== 'all' && s.grade !== filterGrade) return false;
    if (search && !s.name?.toLowerCase().includes(search.toLowerCase()) && !s.email?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {};
  Object.values(STATUS_LABELS).forEach(({ label }) => { counts[label] = 0; });
  rosterList.forEach(s => { const info = STATUS_LABELS[s.status]; if (info) counts[info.label] = (counts[info.label] || 0) + 1; });

  return (
    <div>
      {/* Survey Ingestion */}
      <div style={s.card}>
        <div style={s.sectionTitle}>1. Load Main Coco Survey</div>
        <div style={{ ...s.row, marginBottom: 12 }}>
          <div>
            <span style={s.label}>Survey ID</span>
            <input style={{ ...s.input, width: 320 }} value={surveyId} onChange={e => setSurveyId(e.target.value)} placeholder="Firestore survey doc ID" />
          </div>
          <div style={{ marginTop: 16 }}>
            <button style={s.btn()} onClick={ingestMainSurvey} disabled={ingestingMain}>
              {ingestingMain ? 'Loading…' : rosterList.length > 0 ? 'Re-Ingest' : 'Ingest Responses'}
            </button>
          </div>
        </div>
        <div style={s.sectionTitle}>2. Load Exemptions Survey</div>
        <div style={s.row}>
          <div>
            <span style={s.label}>Exemptions Survey ID</span>
            <input style={{ ...s.input, width: 320 }} value={exemptSurveyId} onChange={e => setExemptSurveyId(e.target.value)} placeholder="Firestore survey doc ID" />
          </div>
          <div style={{ marginTop: 16 }}>
            <button style={s.btn()} onClick={ingestExemptions} disabled={ingestingExempt}>
              {ingestingExempt ? 'Loading…' : 'Apply Exemptions'}
            </button>
          </div>
        </div>
      </div>

      {/* Add team sport players */}
      <div style={s.card}>
        <div style={s.sectionTitle}>3. Add Soccer / Volleyball Players</div>
        <p style={{ fontSize: 13, color: 'var(--ka-gray-500)', marginBottom: 12 }}>
          Paste a CSV of emails (one per line or comma-separated) to mark those students as offseason training.
        </p>
        <div style={{ ...s.row, alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <span style={s.label}>Sport</span>
            <select style={s.select} value={teamSport} onChange={e => setTeamSport(e.target.value)}>
              <option>Volleyball</option>
              <option>Football</option>
              <option>Both Volleyball and Football</option>
            </select>
          </div>
        </div>
        <textarea
          style={{ ...s.input, width: '100%', height: 80, resize: 'vertical', marginBottom: 8, boxSizing: 'border-box' }}
          placeholder="student1@kingsacademy.edu.jo, student2@kingsacademy.edu.jo, ..."
          value={csvText} onChange={e => setCsvText(e.target.value)}
        />
        <button style={s.btn()} onClick={addTeamSportPlayers}>Mark as Offseason</button>
      </div>

      {/* Stats */}
      {rosterList.length > 0 && (
        <div style={{ ...s.card, padding: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={s.stat}><div style={s.statN}>{rosterList.length}</div><div style={s.statL}>Total</div></div>
            {Object.entries(STATUS_LABELS).map(([key, { label, color }]) => {
              const n = rosterList.filter(r => r.status === key).length;
              return n > 0 ? (
                <div key={key} style={s.stat}>
                  <div style={{ ...s.statN, color }}>{n}</div>
                  <div style={s.statL}>{label}</div>
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}

      {/* Roster table */}
      {rosterList.length > 0 && (
        <div style={s.card}>
          <div style={{ ...s.row, marginBottom: 12 }}>
            <input style={{ ...s.input, width: 200 }} placeholder="Search name / email…" value={search} onChange={e => setSearch(e.target.value)} />
            <select style={s.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">All statuses</option>
              {Object.entries(STATUS_LABELS).map(([k, { label }]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <select style={s.select} value={filterGrade} onChange={e => setFilterGrade(e.target.value)}>
              <option value="all">All grades</option>
              {['9', '10', '11', '12'].map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--ka-gray-500)', marginLeft: 'auto' }}>{filtered.length} shown</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Grade</th>
                  <th style={s.th}>SIS ID</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>1st</th>
                  <th style={s.th}>2nd</th>
                  <th style={s.th}>3rd</th>
                  <th style={s.th}>Team Sport</th>
                  {canManage && <th style={s.th}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(student => {
                  const info = STATUS_LABELS[student.status] || { label: student.status, color: '#6b7280' };
                  return (
                    <tr key={student.id}>
                      <td style={s.td}>
                        <div style={{ fontWeight: 500 }}>{student.name || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--ka-gray-500)' }}>{student.email}</div>
                      </td>
                      <td style={s.td}>{student.grade || '—'}</td>
                      <td style={s.td}>{student.sisId || <span style={{ color: '#dc2626' }}>Missing</span>}</td>
                      <td style={s.td}><span style={s.badge(info.color)}>{info.label}</span></td>
                      <td style={s.td}>{student.choice1 || '—'}</td>
                      <td style={s.td}>{student.choice2 || '—'}</td>
                      <td style={s.td}>{student.choice3 || '—'}</td>
                      <td style={s.td}>{student.teamSport || '—'}</td>
                      {canManage && (
                        <td style={s.td}>
                          <select
                            style={{ ...s.select, fontSize: 11, padding: '3px 6px' }}
                            value={student.status}
                            onChange={e => toggleStatus(student.id, e.target.value)}
                          >
                            {Object.entries(STATUS_LABELS).map(([k, { label }]) =>
                              <option key={k} value={k}>{label}</option>
                            )}
                          </select>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 2 — Activities & Staff
// ═══════════════════════════════════════════════════════════════════════════
function ActivitiesTab({ activities, setActivities, canManage, flash, saving, setSaving }) {
  const [editing, setEditing] = useState(null); // activity name being edited
  const [staffName, setStaffName] = useState('');
  const [staffRole, setStaffRole] = useState('');
  const [staffIsAdult, setStaffIsAdult] = useState(true);

  const saveCapacity = async (name, capacity) => {
    setSaving(true);
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', name),
      { capacity: Number(capacity), staff: activities[name]?.staff || [] }, { merge: true });
    setActivities(a => ({ ...a, [name]: { ...a[name], capacity: Number(capacity) } }));
    setSaving(false);
  };

  const addStaff = async (name) => {
    if (!staffName.trim()) return;
    const member = { name: staffName.trim(), role: staffRole.trim(), isAdult: staffIsAdult };
    const updated = [...(activities[name]?.staff || []), member];
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', name),
      { staff: updated }, { merge: true });
    setActivities(a => ({ ...a, [name]: { ...a[name], staff: updated } }));
    setStaffName(''); setStaffRole('');
    flash('success', `Added ${member.name} to ${name}.`);
  };

  const removeStaff = async (actName, idx) => {
    const updated = activities[actName].staff.filter((_, i) => i !== idx);
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', actName), { staff: updated }, { merge: true });
    setActivities(a => ({ ...a, [actName]: { ...a[actName], staff: updated } }));
  };

  const totalCapacity = Object.values(activities).reduce((sum, a) => sum + (a.capacity || 0), 0);

  return (
    <div>
      <div style={{ ...s.card, padding: 14, marginBottom: 16 }}>
        <div style={s.row}>
          <div style={s.stat}><div style={s.statN}>{ACTIVITIES.length}</div><div style={s.statL}>Activities</div></div>
          <div style={s.stat}><div style={s.statN}>{totalCapacity}</div><div style={s.statL}>Total Capacity</div></div>
        </div>
      </div>

      {ACTIVITIES.map(name => {
        const act = activities[name] || { capacity: 20, staff: [] };
        const isOpen = editing === name;
        return (
          <div key={name} style={s.card}>
            <div style={{ ...s.row, marginBottom: isOpen ? 16 : 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{name}</div>
              <div style={s.row}>
                <span style={{ fontSize: 12, color: 'var(--ka-gray-500)' }}>Capacity:</span>
                <input
                  type="number" min={1} max={200}
                  style={{ ...s.input, width: 60 }}
                  defaultValue={act.capacity}
                  onBlur={e => saveCapacity(name, e.target.value)}
                />
              </div>
              {act.staff.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {act.staff.map((m, i) => (
                    <span key={i} style={s.badge(m.isAdult ? '#2563eb' : '#6b7280')}>
                      {m.name}{m.role ? ` · ${m.role}` : ''}{m.isAdult ? ' 👤' : ''}
                      {canManage && <span style={{ cursor: 'pointer', marginLeft: 4 }} onClick={() => removeStaff(name, i)}>×</span>}
                    </span>
                  ))}
                </div>
              )}
              {canManage && (
                <button style={s.btn('ghost')} onClick={() => setEditing(isOpen ? null : name)}>
                  {isOpen ? 'Done' : '+ Staff'}
                </button>
              )}
            </div>

            {isOpen && (
              <div style={{ borderTop: '1px solid var(--ka-gray-100)', paddingTop: 14 }}>
                <div style={s.sectionTitle}>Add Staff / Coach</div>
                <div style={s.row}>
                  <div>
                    <span style={s.label}>Name</span>
                    <input style={{ ...s.input, width: 180 }} value={staffName} onChange={e => setStaffName(e.target.value)} placeholder="Full name" />
                  </div>
                  <div>
                    <span style={s.label}>Role / Title</span>
                    <input style={{ ...s.input, width: 150 }} value={staffRole} onChange={e => setStaffRole(e.target.value)} placeholder="e.g. Coach, Supervisor" />
                  </div>
                  <div>
                    <span style={s.label}>Type</span>
                    <select style={s.select} value={staffIsAdult ? 'adult' : 'student'} onChange={e => setStaffIsAdult(e.target.value === 'adult')}>
                      <option value="adult">Adult (Teacher/Coach)</option>
                      <option value="student">Student Leader</option>
                    </select>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <button style={s.btn()} onClick={() => addStaff(name)}>Add</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 3 — Scheduler
// ═══════════════════════════════════════════════════════════════════════════
function runScheduler(roster, activities) {
  // Counts of placed students per activity
  const counts = {};
  ACTIVITIES.forEach(a => { counts[a] = 0; });

  const result = {};
  Object.entries(roster).forEach(([id, s]) => {
    result[id] = { ...s, placed: null, placedRound: null };
  });

  // Only place students with status 'active' or 'offseason-opted-in'
  const eligible = Object.entries(result).filter(([, s]) =>
    s.status === 'active' || s.status === 'offseason-opted-in'
  );

  // 3 rounds
  for (let round = 1; round <= 3; round++) {
    const choiceKey = ['choice1', 'choice2', 'choice3'][round - 1];
    for (const [id, student] of eligible) {
      if (result[id].placed) continue; // already placed
      const choice = student[choiceKey];
      if (!choice) continue;
      const cap = activities[choice]?.capacity || 20;
      if (counts[choice] < cap) {
        result[id].placed = choice;
        result[id].placedRound = round;
        counts[id] = counts[id]; // no-op; placement tracked via result
        counts[choice]++;
      }
    }
  }

  // Mark unplaced
  eligible.forEach(([id]) => {
    if (!result[id].placed) result[id] = { ...result[id], status: 'unplaced' };
  });

  return { result, counts };
}

function SchedulerTab({ roster, setRoster, activities, config, setConfig, canManage, flash, committed, saving, setSaving }) {
  const [preview, setPreview] = useState(null); // { result, counts } after a run
  const [overrideId, setOverrideId] = useState('');
  const [overrideActivity, setOverrideActivity] = useState('');

  const eligible = Object.values(roster).filter(s => s.status === 'active' || s.status === 'offseason-opted-in');
  const inPool = eligible.length;

  const runPreview = () => {
    const { result, counts } = runScheduler(roster, activities);
    setPreview({ result, counts });
  };

  const applyAndSave = async () => {
    if (!preview) return;
    setSaving(true);
    const batch = writeBatch(db);
    Object.entries(preview.result).forEach(([id, data]) => {
      batch.set(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), {
        placed: data.placed,
        placedRound: data.placedRound,
        status: data.status,
        manualOverride: data.manualOverride || null,
      }, { merge: true });
    });
    await batch.commit();
    setRoster(preview.result);
    setPreview(null);
    flash('success', 'Schedule saved to Firestore. You can run it again or commit when ready.');
    setSaving(false);
  };

  const commitSchedule = async () => {
    if (!window.confirm('Commit this schedule? Students and teachers will be able to see assignments. You can still make manual adjustments after.')) return;
    setSaving(true);
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID), { status: 'committed' }, { merge: true });
    setConfig(c => ({ ...c, status: 'committed' }));
    flash('success', 'Schedule committed! Students and teachers can now see their assignments.');
    setSaving(false);
  };

  const applyManualOverride = async () => {
    if (!overrideId || !overrideActivity) return;
    const student = roster[overrideId];
    if (!student) return flash('warn', 'Student ID not found.');
    await updateDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', overrideId), {
      placed: overrideActivity, placedRound: null, manualOverride: overrideActivity, status: 'active',
    });
    setRoster(r => ({ ...r, [overrideId]: { ...r[overrideId], placed: overrideActivity, placedRound: null, manualOverride: overrideActivity } }));
    setOverrideId(''); setOverrideActivity('');
    flash('success', `Manually placed ${student.name || overrideId} in ${overrideActivity}.`);
  };

  // Activity load from either preview or saved roster
  const source = preview ? preview.result : roster;
  const actCounts = {};
  ACTIVITIES.forEach(a => { actCounts[a] = 0; });
  Object.values(source).forEach(s => { if (s.placed) actCounts[s.placed] = (actCounts[s.placed] || 0) + 1; });

  const placed = Object.values(source).filter(s => s.placed).length;
  const unplaced = Object.values(source).filter(s => s.status === 'unplaced').length;
  const r1 = Object.values(source).filter(s => s.placedRound === 1).length;
  const r2 = Object.values(source).filter(s => s.placedRound === 2).length;
  const r3 = Object.values(source).filter(s => s.placedRound === 3).length;

  return (
    <div>
      {/* Controls */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Run Scheduler</div>
        <p style={{ fontSize: 13, color: 'var(--ka-gray-500)', marginBottom: 14 }}>
          {inPool} students in pool (active + offseason opted-in). Basketball, exempt, and offseason students are excluded unless their status is changed.
        </p>
        <div style={s.row}>
          <button style={s.btn()} onClick={runPreview}>▶ Run Scheduler (Preview)</button>
          {preview && <button style={s.btn('success')} onClick={applyAndSave} disabled={saving}>💾 Save Results</button>}
          {!preview && placed > 0 && !committed && canManage && (
            <button style={s.btn('success')} onClick={commitSchedule} disabled={saving}>✓ Commit Schedule</button>
          )}
          {committed && canManage && (
            <button style={{ ...s.btn('ghost') }} onClick={commitSchedule}>Re-commit</button>
          )}
        </div>
        {preview && (
          <div style={{ ...s.warn, marginTop: 14, marginBottom: 0 }}>
            This is a preview — click "Save Results" to persist to Firestore.
          </div>
        )}
      </div>

      {/* Stats */}
      {(preview || placed > 0) && (
        <div style={{ ...s.card, padding: 14 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={s.stat}><div style={s.statN}>{placed}</div><div style={s.statL}>Placed</div></div>
            <div style={s.stat}><div style={{ ...s.statN, color: '#dc2626' }}>{unplaced}</div><div style={s.statL}>Unplaced</div></div>
            <div style={s.stat}><div style={{ ...s.statN, color: '#059669' }}>{r1}</div><div style={s.statL}>1st Choice</div></div>
            <div style={s.stat}><div style={{ ...s.statN, color: '#d97706' }}>{r2}</div><div style={s.statL}>2nd Choice</div></div>
            <div style={s.stat}><div style={{ ...s.statN, color: '#6b7280' }}>{r3}</div><div style={s.statL}>3rd Choice</div></div>
          </div>
        </div>
      )}

      {/* Activity load */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Activity Loads {preview ? '(Preview)' : '(Current)'}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Activity</th>
                <th style={s.th}>Enrolled</th>
                <th style={s.th}>Capacity</th>
                <th style={s.th}>Available</th>
                <th style={s.th}>Fill</th>
                <th style={s.th}>Staff</th>
              </tr>
            </thead>
            <tbody>
              {ACTIVITIES.map(name => {
                const cap = activities[name]?.capacity || 20;
                const enrolled = actCounts[name] || 0;
                const pct = Math.round((enrolled / cap) * 100);
                const full = enrolled >= cap;
                return (
                  <tr key={name}>
                    <td style={s.td}><strong>{name}</strong></td>
                    <td style={s.td}>{enrolled}</td>
                    <td style={s.td}>{cap}</td>
                    <td style={s.td}>{full ? <span style={{ color: '#dc2626', fontWeight: 600 }}>FULL</span> : cap - enrolled}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 80, height: 6, background: 'var(--ka-gray-200)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: full ? '#dc2626' : '#2563eb', borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--ka-gray-500)' }}>{pct}%</span>
                      </div>
                    </td>
                    <td style={s.td}>
                      {(activities[name]?.staff || []).map((m, i) => (
                        <span key={i} style={{ ...s.badge(m.isAdult ? '#2563eb' : '#6b7280'), marginRight: 4 }}>{m.name}</span>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual overrides */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Manual Placement Override</div>
        <p style={{ fontSize: 13, color: 'var(--ka-gray-500)', marginBottom: 12 }}>
          Override a student's placement after running the scheduler. Enter their SIS ID or roster doc ID.
        </p>
        <div style={s.row}>
          <div>
            <span style={s.label}>Student SIS ID</span>
            <input style={{ ...s.input, width: 140 }} value={overrideId} onChange={e => setOverrideId(e.target.value)} placeholder="e.g. 166985" />
          </div>
          <div>
            <span style={s.label}>Place in Activity</span>
            <select style={s.select} value={overrideActivity} onChange={e => setOverrideActivity(e.target.value)}>
              <option value="">— select —</option>
              {ACTIVITIES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div style={{ marginTop: 16 }}>
            <button style={s.btn()} onClick={applyManualOverride}>Apply Override</button>
          </div>
        </div>

        {/* Unplaced students quick-assign */}
        {Object.values(source).some(s => s.status === 'unplaced') && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#dc2626' }}>Unplaced Students</div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Grade</th>
                  <th style={s.th}>Choices</th>
                  <th style={s.th}>Assign</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(source).filter(([, s]) => s.status === 'unplaced').map(([id, student]) => (
                  <UnplacedRow key={id} id={id} student={student} activities={activities} actCounts={actCounts} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function UnplacedRow({ id, student, activities, actCounts }) {
  const [pick, setPick] = useState('');
  const assign = async () => {
    if (!pick) return;
    await updateDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), {
      placed: pick, placedRound: null, manualOverride: pick, status: 'active',
    });
    setPick('');
  };
  return (
    <tr>
      <td style={s.td}>{student.name || id}</td>
      <td style={s.td}>{student.grade || '—'}</td>
      <td style={{ ...s.td, fontSize: 12 }}>
        {[student.choice1, student.choice2, student.choice3].filter(Boolean).join(' → ')}
      </td>
      <td style={s.td}>
        <div style={{ display: 'flex', gap: 6 }}>
          <select style={{ ...s.select, fontSize: 12 }} value={pick} onChange={e => setPick(e.target.value)}>
            <option value="">— assign —</option>
            {ACTIVITIES.map(a => {
              const cap = activities[a]?.capacity || 20;
              const enrolled = actCounts[a] || 0;
              return <option key={a} value={a}>{a} ({enrolled}/{cap})</option>;
            })}
          </select>
          <button style={{ ...s.btn(), padding: '5px 10px', fontSize: 12 }} onClick={assign}>Place</button>
        </div>
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 4 — Results
// ═══════════════════════════════════════════════════════════════════════════
function ResultsTab({ roster, activities, config, flash, saving, setSaving }) {
  const [view, setView] = useState('byActivity');
  const [filterGrade, setFilterGrade] = useState('all');

  const committed = config?.status === 'committed';
  const rosterList = Array.isArray(roster) ? roster : Object.entries(roster).map(([id, d]) => ({ id, ...d }));

  const byActivity = {};
  ACTIVITIES.forEach(a => { byActivity[a] = []; });
  rosterList.forEach(s => { if (s.placed && byActivity[s.placed]) byActivity[s.placed].push(s); });

  const downloadCSV = () => {
    const rows = [['Name', 'SIS ID', 'Grade', 'Email', 'Status', 'Assigned Activity', 'Round', 'Manual Override', '1st Choice', '2nd Choice', '3rd Choice']];
    rosterList.forEach(s => {
      rows.push([
        s.name, s.sisId, s.grade, s.email,
        STATUS_LABELS[s.status]?.label || s.status,
        s.placed || '', s.placedRound || '', s.manualOverride ? 'Yes' : 'No',
        s.choice1, s.choice2, s.choice3,
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'coco-assignments.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {!committed && (
        <div style={s.warn}>Schedule is not yet committed. Only admins can see this view. Commit the schedule in the Scheduler tab to make it visible to students and teachers.</div>
      )}

      <div style={{ ...s.card, padding: 14, marginBottom: 16 }}>
        <div style={s.row}>
          <button style={s.tab(view === 'byActivity')} onClick={() => setView('byActivity')}>By Activity</button>
          <button style={s.tab(view === 'byStudent')} onClick={() => setView('byStudent')}>By Student</button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <select style={s.select} value={filterGrade} onChange={e => setFilterGrade(e.target.value)}>
              <option value="all">All grades</option>
              {['9', '10', '11', '12'].map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
            <button style={s.btn('ghost')} onClick={downloadCSV}>⬇ CSV Export</button>
          </div>
        </div>
      </div>

      {view === 'byActivity' && (
        <div>
          {ACTIVITIES.map(name => {
            const students = byActivity[name].filter(s => filterGrade === 'all' || s.grade === filterGrade);
            const cap = activities[name]?.capacity || 20;
            const staff = activities[name]?.staff || [];
            return (
              <div key={name} style={s.card}>
                <div style={{ ...s.row, marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{name}</div>
                  <span style={s.badge(students.length >= cap ? '#dc2626' : '#059669')}>{students.length} / {cap}</span>
                  {staff.length > 0 && staff.filter(m => m.isAdult).map((m, i) => (
                    <span key={i} style={s.badge('#2563eb')}>👤 {m.name}{m.role ? ` · ${m.role}` : ''}</span>
                  ))}
                </div>
                {students.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--ka-gray-400)' }}>No students assigned.</p>
                ) : (
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Name</th>
                        <th style={s.th}>Grade</th>
                        <th style={s.th}>SIS ID</th>
                        <th style={s.th}>Round</th>
                        <th style={s.th}>Override</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.sort((a, b) => Number(a.grade) - Number(b.grade)).map(student => (
                        <tr key={student.id}>
                          <td style={s.td}>{student.name || '—'}</td>
                          <td style={s.td}>{student.grade || '—'}</td>
                          <td style={s.td}>{student.sisId || '—'}</td>
                          <td style={s.td}>
                            {student.placedRound
                              ? <span style={s.badge(student.placedRound === 1 ? '#059669' : student.placedRound === 2 ? '#d97706' : '#6b7280')}>
                                  {student.placedRound === 1 ? '1st' : student.placedRound === 2 ? '2nd' : '3rd'} choice
                                </span>
                              : '—'}
                          </td>
                          <td style={s.td}>{student.manualOverride ? <span style={s.badge('#7c3aed')}>Manual</span> : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === 'byStudent' && (
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Name</th>
                <th style={s.th}>Grade</th>
                <th style={s.th}>SIS ID</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Assigned</th>
                <th style={s.th}>Round</th>
              </tr>
            </thead>
            <tbody>
              {rosterList
                .filter(s => filterGrade === 'all' || s.grade === filterGrade)
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map(student => {
                  const info = STATUS_LABELS[student.status] || { label: student.status, color: '#6b7280' };
                  return (
                    <tr key={student.id}>
                      <td style={s.td}><strong>{student.name || '—'}</strong></td>
                      <td style={s.td}>{student.grade || '—'}</td>
                      <td style={s.td}>{student.sisId || '—'}</td>
                      <td style={s.td}><span style={s.badge(info.color)}>{info.label}</span></td>
                      <td style={s.td}>{student.placed || <span style={{ color: 'var(--ka-gray-400)' }}>—</span>}</td>
                      <td style={s.td}>
                        {student.placedRound
                          ? <span style={s.badge(student.placedRound === 1 ? '#059669' : student.placedRound === 2 ? '#d97706' : '#6b7280')}>
                              {student.placedRound === 1 ? '1st' : student.placedRound === 2 ? '2nd' : '3rd'}
                            </span>
                          : student.manualOverride ? <span style={s.badge('#7c3aed')}>Manual</span> : '—'}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}